// ─────────────────────────────────────────────────────────────────────────────
// COÛT DE REVIENT INDUSTRIEL (CRI) — moteur de calcul PUR.
//
// Aucune dépendance : ce fichier est importé par l'application React ET chargé
// tel quel par le script de tests Node (/app/tests/cri.test.mjs).
//
// SÉPARATION ABSOLUE : deux composantes toujours distinctes.
//   • Coût matière        = achat des olives rapporté à l'huile réellement produite.
//   • Coût transformation = électricité, main-d'œuvre, maintenance, autres charges
//                           industrielles, appliqué au standard puis régularisé.
// Une facture d'usine, un changement de standard ou une régularisation ne
// modifient JAMAIS le coût matière, donc jamais le CMP Achat des cuves.
// ─────────────────────────────────────────────────────────────────────────────

// Arrondi monétaire TND à 3 décimales, symétrique pour les valeurs négatives.
export const r3 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  const s = n < 0 ? -1 : 1;
  return (s * Math.round(Math.abs(n) * 1000)) / 1000;
};

export const CATEGORIES_INDUSTRIELLES = [
  'Électricité',
  'Main-d’œuvre',
  'Maintenance',
  'Autres charges industrielles',
];

// ───────── Standards industriels ─────────

// Un standard se calcule à partir des coûts variables prévus par tonne et des
// charges fixes rapportées à une capacité normale réaliste.
export function construireStandard({ categories = [], capaciteNormaleT = 0 }) {
  const cap = Number(capaciteNormaleT) || 0;
  const lignes = categories
    .map((c) => {
      const variable = r3(c.variableTndParTonne);
      const fixe = r3(c.fixeTnd);
      const partFixe = cap > 0 ? r3(fixe / cap) : 0;
      return {
        categorie: c.categorie,
        variableTndParTonne: variable,
        fixeTnd: fixe,
        fixeTndParTonne: partFixe,
        tauxTndParTonne: r3(variable + partFixe),
      };
    })
    .filter((l) => l.categorie && (l.variableTndParTonne > 0 || l.fixeTnd > 0));
  return {
    categories: lignes,
    capaciteNormaleT: cap,
    tauxTndParTonne: r3(lignes.reduce((s, l) => s + l.tauxTndParTonne, 0)),
    fixesTnd: r3(lignes.reduce((s, l) => s + l.fixeTnd, 0)),
  };
}

export const tauxStandardTotal = (standard) =>
  r3((standard?.categories ?? []).reduce((s, c) => s + (Number(c.tauxTndParTonne) || 0), 0));

// ───────── Base de charges incorporable ─────────

// Paramétrage métier : TVA des charges industrielles NON RÉCUPÉRABLE à 100 %.
// La facture est donc incorporée pour son montant TTC (HT + autres taxes +
// timbre + TVA non récupérable), sans jamais compter deux fois une composante.
export function chargeIncorporable({
  ht = 0,
  montantTVA = 0,
  autresTaxes = 0,
  timbreFiscal = 0,
  partTvaNonRecuperable = 1,
}) {
  const part = Math.min(1, Math.max(0, Number(partTvaNonRecuperable)));
  const tva = r3(montantTVA);
  const tvaNonRecuperable = r3(tva * part);
  return {
    ht: r3(ht),
    montantTVA: tva,
    autresTaxes: r3(autresTaxes),
    timbreFiscal: r3(timbreFiscal),
    partTvaNonRecuperable: part,
    tvaNonRecuperable,
    montantIncorporable: r3(r3(ht) + r3(autresTaxes) + r3(timbreFiscal) + tvaNonRecuperable),
  };
}

// Salaires et pointages : aucune TVA à ajouter, on retient le coût de production
// (brut + charges patronales applicables). Un pointage déjà repris dans la paie
// ne doit jamais être compté une seconde fois (anti-doublon par pièce source).
export function coutSalarial({ brut = 0, tauxChargesPatronales = 0 }) {
  const b = r3(brut);
  const t = Number(tauxChargesPatronales) || 0;
  const charges = r3((b * t) / 100);
  return { brut: b, tauxChargesPatronales: t, chargesPatronales: charges, montantIncorporable: r3(b + charges) };
}

// Rattachement à la PÉRIODE DE CONSOMMATION / DE SERVICE, jamais à la date de
// paiement : une facture STEG trimestrielle ne charge pas l'OF du jour de saisie.
export const chevauche = (d1, f1, d2, f2) =>
  String(d1 || '0000-01-01') <= String(f2 || '9999-12-31') &&
  String(f1 || '9999-12-31') >= String(d2 || '0000-01-01');

export const chargeImputable = (charge, { du = '', au = '' } = {}) =>
  chevauche(charge.periodeDebut, charge.periodeFin, du || '0000-01-01', au || '9999-12-31');

// ───────── Coûts unitaires ─────────

// M / H — le bonus de rendement FOSS appartient à l'huilerie et entre dans H :
// il réduit naturellement le coût matière unitaire, sans revenu fictif ajouté.
export const coutMatiereUnitaire = (M, H) => (Number(H) > 0 ? r3(Number(M) / Number(H)) : null);

// F = Q × S
export const fraisStandards = (tonnes, taux) => r3((Number(tonnes) || 0) * (Number(taux) || 0));

export const criStandardUnitaire = (M, F, base) =>
  Number(base) > 0 ? r3((Number(M) + Number(F)) / Number(base)) : null;

export const criRegulariseUnitaire = (M, F, dF, base) =>
  Number(base) > 0 ? r3((Number(M) + Number(F) + Number(dF)) / Number(base)) : null;

// ───────── Écarts et régularisation ─────────

// Écart = charges réelles imputables − total des frais standards imputés.
// Positif = défavorable (charges supplémentaires), négatif = économie.
export const ecartIndustriel = (reelles, standardsImputes) => r3(Number(reelles) - Number(standardsImputes));

// Clôture idempotente : si des régularisations mensuelles ont déjà été
// comptabilisées, la clôture ne comptabilise que le SOLDE restant.
export const soldeRegularisation = (ecartTotal, dejaComptabilise = 0) =>
  r3(Number(ecartTotal) - Number(dejaComptabilise));

// Répartition au prorata des tonnes, conservant EXACTEMENT le total à répartir
// (méthode des plus grands restes au millième).
export function repartirProrata(total, poids = []) {
  const T = r3(total);
  const p = poids.map((v) => Number(v) || 0);
  const somme = p.reduce((s, v) => s + v, 0);
  if (!p.length || somme <= 0) return { parts: p.map(() => 0), reste: T, total: T };
  const signe = T < 0 ? -1 : 1;
  const brut = p.map((v) => (T * v) / somme);
  const base = brut.map((v) => (signe * Math.floor(Math.abs(v) * 1000 + 1e-9)) / 1000);
  const frac = brut.map((v, i) => Math.abs(v) * 1000 + 1e-9 - Math.abs(base[i]) * 1000);
  const manque = Math.round(Math.abs(T) * 1000 - base.reduce((s, v) => s + Math.abs(v) * 1000, 0));
  const ordre = frac.map((f, i) => [f, i]).sort((a, b) => b[0] - a[0]).map(([, i]) => i);
  const parts = [...base];
  for (let k = 0; k < manque; k += 1) {
    const i = ordre[k % ordre.length];
    parts[i] = r3(parts[i] + signe * 0.001);
  }
  const arrondies = parts.map(r3);
  return { parts: arrondies, reste: r3(T - arrondies.reduce((s, v) => s + v, 0)), total: T };
}

// Frais fixes non absorbés par la sous-activité : présentés SÉPARÉMENT dans le
// résultat de campagne, jamais dilués dans le CRI des OF.
export function sousActivite({ capaciteNormaleT = 0, tonnesReelles = 0, fixesTnd = 0 }) {
  const cap = Number(capaciteNormaleT) || 0;
  const q = Number(tonnesReelles) || 0;
  if (cap <= 0) return { tauxActivite: null, fixesNonAbsorbes: 0 };
  if (q >= cap) return { tauxActivite: r3(q / cap), fixesNonAbsorbes: 0 };
  return { tauxActivite: r3(q / cap), fixesNonAbsorbes: r3(Number(fixesTnd) * (1 - q / cap)) };
}

// ───────── Fiche OF : lecture A → B → C → D ─────────
//
//   A — Coût olives pur ................ M
//   B — Marge brute matière ............ V − M
//   C — Frais industriels standards .... F
//   D — Marge nette industrielle ....... V − M − F
//   Régularisation ..................... ΔF
//   Marge nette régularisée ............ V − M − F − ΔF
//
// Les totaux sont en TND ; les indicateurs unitaires en TND/kg sont affichés à
// côté et ne sont JAMAIS soustraits d'un total en TND.
export function ficheOf({
  type = 'achat',
  coutOlives = 0,
  huileKg = 0,
  tonnes = 0,
  tauxStandard = 0,
  deltaF = 0,
  valeurCommerciale = 0,
  produitPrestation = 0,
}) {
  const prestation = type === 'prestation';
  const M = prestation ? 0 : r3(coutOlives);
  const H = Number(huileKg) || 0;
  const Q = Number(tonnes) || 0;
  const F = fraisStandards(Q, tauxStandard);
  const dF = r3(deltaF);
  const V = prestation ? r3(produitPrestation) : r3(valeurCommerciale);
  // Base unitaire : kg d'huile pour un OF d'achat, kg d'olives traitées pour une
  // prestation (l'huile appartient au client : ni achat matière, ni produit de vente).
  const base = prestation ? r3(Q * 1000) : H;
  return {
    type,
    M,
    H,
    Q,
    F,
    deltaF: dF,
    V,
    A: M,
    B: r3(V - M),
    C: F,
    D: r3(V - M - F),
    margeRegularisee: r3(V - M - F - dF),
    fraisIndustrielsDefinitifs: r3(F + dF),
    coutMatiereUnitaire: prestation ? null : coutMatiereUnitaire(M, H),
    coutTransformationUnitaire: base > 0 ? r3(F / base) : null,
    criStandardUnitaire: criStandardUnitaire(M, F, base),
    criRegulariseUnitaire: criRegulariseUnitaire(M, F, dF, base),
    uniteCri: prestation ? 'TND/kg d’olives' : 'TND/kg d’huile',
    baseUnitaire: base,
    cmpAchatApplicable: !prestation,
    coutNonCalculable: !(base > 0),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TESTS MÉTIER — scénario fictif imposé + 7 contrôles de non-régression.
// Exécutables dans l'application (onglet « Tests métier ») et en Node.
// ─────────────────────────────────────────────────────────────────────────────

const STANDARD_TEST = construireStandard({
  capaciteNormaleT: 15,
  categories: [
    { categorie: 'Électricité', variableTndParTonne: 30 },
    { categorie: 'Main-d’œuvre', variableTndParTonne: 50 },
    { categorie: 'Maintenance', variableTndParTonne: 20 },
  ],
});

export function executerTestsCri() {
  const lignes = [];
  const controle = (code, libelle, attendu, obtenu, tolerance = 0.0005) => {
    const ok =
      attendu === null || obtenu === null
        ? attendu === obtenu
        : typeof attendu === 'string' || typeof attendu === 'boolean'
          ? attendu === obtenu
          : Math.abs(Number(attendu) - Number(obtenu)) <= tolerance;
    lignes.push({ code, libelle, attendu, obtenu, ok });
    return ok;
  };

  const S = STANDARD_TEST.tauxTndParTonne;
  controle('STD-1', 'Standard total électricité + MO + maintenance', 100, S);

  // OF 1 — Achat : 10 t traitées, FOSS 18 %, olives à 1,440 TND/kg, rendement
  // réel 20 % → 2 000 kg d'huile (dont 200 kg de bonus), vendus à 10 TND/kg.
  const of1 = ficheOf({
    type: 'achat',
    coutOlives: 14400,
    huileKg: 2000,
    tonnes: 10,
    tauxStandard: S,
    valeurCommerciale: 20000,
  });
  controle('OF1-1', 'OF Achat — coût matière total (TND)', 14400, of1.A);
  controle('OF1-2', 'OF Achat — coût matière unitaire (TND/kg huile)', 7.2, of1.coutMatiereUnitaire);
  controle('OF1-3', 'OF Achat — marge brute matière B (TND)', 5600, of1.B);
  controle('OF1-4', 'OF Achat — frais industriels standards C (TND)', 1000, of1.C);
  controle('OF1-5', 'OF Achat — CRI standard unitaire (TND/kg huile)', 7.7, of1.criStandardUnitaire);
  controle('OF1-6', 'OF Achat — marge nette industrielle D (TND)', 4600, of1.D);

  // OF 2 — Prestation : 5 t à 0,180 TND/kg d'olives = 900 TND HT.
  const of2 = ficheOf({ type: 'prestation', huileKg: 0, tonnes: 5, tauxStandard: S, produitPrestation: 900 });
  controle('OF2-1', 'OF Prestation — coût matière (TND)', 0, of2.A);
  controle('OF2-2', 'OF Prestation — CMP Achat non applicable', false, of2.cmpAchatApplicable);
  controle('OF2-3', 'OF Prestation — marge brute matière B (TND)', 900, of2.B);
  controle('OF2-4', 'OF Prestation — frais industriels standards C (TND)', 500, of2.C);
  controle('OF2-5', 'OF Prestation — CRI unitaire (TND/kg olives)', 0.1, of2.criStandardUnitaire);
  controle('OF2-6', 'OF Prestation — marge industrielle D (TND)', 400, of2.D);

  // Charges réelles de la mini-campagne : 600 TTC + 750 salaires + 450 TTC.
  const chargesReelles = r3(600 + 750 + 450);
  const standardsImputes = r3(of1.F + of2.F);
  const ecart = ecartIndustriel(chargesReelles, standardsImputes);
  controle('REG-1', 'Charges réelles imputables (TND)', 1800, chargesReelles);
  controle('REG-2', 'Frais standards imputés (TND)', 1500, standardsImputes);
  controle('REG-3', 'Écart industriel défavorable (TND)', 300, ecart);

  const rep = repartirProrata(ecart, [of1.Q, of2.Q]);
  controle('REG-4', 'ΔF OF Achat au prorata des tonnes (TND)', 200, rep.parts[0]);
  controle('REG-5', 'ΔF OF Prestation au prorata des tonnes (TND)', 100, rep.parts[1]);
  controle('REG-6', 'Répartition — total conservé exactement', 0, rep.reste);

  const of1r = { ...of1, ...ficheOf({ type: 'achat', coutOlives: 14400, huileKg: 2000, tonnes: 10, tauxStandard: S, deltaF: rep.parts[0], valeurCommerciale: 20000 }) };
  const of2r = ficheOf({ type: 'prestation', tonnes: 5, tauxStandard: S, deltaF: rep.parts[1], produitPrestation: 900 });
  controle('REG-7', 'OF Achat — frais industriels définitifs (TND)', 1200, of1r.fraisIndustrielsDefinitifs);
  controle('REG-8', 'OF Achat — CRI régularisé unitaire (TND/kg huile)', 7.8, of1r.criRegulariseUnitaire);
  controle('REG-9', 'OF Achat — marge industrielle régularisée (TND)', 4400, of1r.margeRegularisee);
  controle('REG-10', 'OF Prestation — frais industriels définitifs (TND)', 600, of2r.fraisIndustrielsDefinitifs);
  controle('REG-11', 'OF Prestation — CRI régularisé (TND/kg olives)', 0.12, of2r.criRegulariseUnitaire);
  controle('REG-12', 'OF Prestation — marge régularisée (TND)', 300, of2r.margeRegularisee);
  controle('REG-13', 'CMP Achat inchangé après régularisation (TND/kg)', 7.2, of1r.coutMatiereUnitaire);
  controle(
    'REG-14',
    'Contrôle global 20 000 + 900 − 14 400 − 1 800 (TND)',
    4700,
    r3(of1r.margeRegularisee + of2r.margeRegularisee)
  );

  // Contrôle 1 — facture 100 HT + 19 TVA non récupérable → 119 incorporés.
  const f = chargeIncorporable({ ht: 100, montantTVA: 19, autresTaxes: 0, timbreFiscal: 0 });
  controle('CTRL-1', 'Facture 100 HT + 19 TVA non récupérable → charge incorporée', 119, f.montantIncorporable);

  // Contrôle 2 — standard modifié (150 TND/t) : aucun impact CMP Achat.
  const of1Std150 = ficheOf({ type: 'achat', coutOlives: 14400, huileKg: 2000, tonnes: 10, tauxStandard: 150, valeurCommerciale: 20000 });
  controle('CTRL-2', 'Standard porté à 150 TND/t → CMP Achat toujours 7,200', 7.2, of1Std150.coutMatiereUnitaire);
  controle('CTRL-2b', 'Standard porté à 150 TND/t → frais industriels 1 500 TND', 1500, of1Std150.C);

  // Contrôle 3 — STEG : date de paiement modifiée, période de consommation identique.
  const steg = { periodeDebut: '2025-10-01', periodeFin: '2025-12-31', montantIncorporable: 600, datePaiement: '2026-02-15' };
  const stegPayeePlusTard = { ...steg, datePaiement: '2026-06-30' };
  controle(
    'CTRL-3',
    'STEG — changement de date de paiement sans effet sur l’imputation',
    chargeImputable(steg, { du: '2025-10-01', au: '2025-12-31' }),
    chargeImputable(stegPayeePlusTard, { du: '2025-10-01', au: '2025-12-31' })
  );
  controle(
    'CTRL-3b',
    'STEG — facture trimestrielle non imputée au seul mois de saisie',
    false,
    chargeImputable(steg, { du: '2026-02-01', au: '2026-02-28' })
  );

  // Contrôle 4 — clôture relancée : aucun cumul d'un même écart.
  const cloture1 = soldeRegularisation(ecart, 0);
  const cloture2 = soldeRegularisation(ecart, cloture1);
  controle('CTRL-4', 'Clôture initiale — solde à comptabiliser (TND)', 300, cloture1);
  controle('CTRL-4b', 'Clôture relancée — aucun solde restant (TND)', 0, cloture2);
  const mensuelles = soldeRegularisation(ecart, 120);
  controle('CTRL-4c', 'Clôture après 120 TND de régularisations mensuelles', 180, mensuelles);

  // Contrôle 5 — huile réelle nulle : coût unitaire non calculable, jamais 0.
  const ofSansHuile = ficheOf({ type: 'achat', coutOlives: 14400, huileKg: 0, tonnes: 10, tauxStandard: S, valeurCommerciale: 0 });
  controle('CTRL-5', 'Huile réelle nulle — coût matière unitaire non calculable', null, ofSansHuile.coutMatiereUnitaire);
  controle('CTRL-5b', 'Huile réelle nulle — CRI non calculable', null, ofSansHuile.criStandardUnitaire);
  controle('CTRL-5c', 'Huile réelle nulle — coût matière total conservé (TND)', 14400, ofSansHuile.A);

  // Contrôle 6 — retenue 1 % : net fournisseur 14 256, coût matière 14 400.
  const retenue = r3(14400 * 0.01);
  controle('CTRL-6', 'Retenue 1 % — net à payer fournisseur (TND)', 14256, r3(14400 - retenue));
  controle('CTRL-6b', 'Retenue 1 % — coût matière conservé (TND)', 14400, of1.A);
  controle('CTRL-6c', 'Retenue 1 % — coût matière unitaire conservé (TND/kg)', 7.2, of1.coutMatiereUnitaire);

  // Contrôle 7 — bonus FOSS : aucun revenu fictif, seule H augmente.
  const huileTheorique = 10000 * 0.18;
  controle('CTRL-7', 'Bonus FOSS — 200 kg inclus dans H sans revenu ajouté', 20000, of1.V);
  controle('CTRL-7b', 'Bonus FOSS — écart huile réelle / théorique (kg)', 200, r3(of1.H - huileTheorique));

  // Contrôle 8 — conservation exacte d'un total non divisible.
  const tiers = repartirProrata(1000, [1, 1, 1]);
  controle('CTRL-8', 'Répartition 1 000 / 3 — total conservé exactement', 1000, r3(tiers.parts.reduce((s, v) => s + v, 0)));

  const reussis = lignes.filter((l) => l.ok).length;
  return { lignes, total: lignes.length, reussis, echecs: lignes.length - reussis };
}

// Synthèse d'affichage extraite de ChargesCri, formule et arrondi inchangés.
export function totauxChargesCri(charges = []) {
  const normales = charges.filter(c => !c.exceptionnel);
  const exceptionnelles = charges.filter(c => c.exceptionnel);
  const somme = lignes => Number(lignes.reduce((s, c) => s + c.montantIncorporable, 0).toFixed(3));
  return {
    normales: somme(normales),
    exceptionnelles: somme(exceptionnelles),
    parCategorie: CATEGORIES_INDUSTRIELLES.map(cat => [cat, somme(normales.filter(c => c.categorie === cat))]),
  };
}
